import { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, DollarSign, Phone } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { SelectableCard } from './SelectableCard';
import {
  INDUSTRIES,
  SUBCATEGORIES,
  REVENUE_TREND_OPTIONS,
  STATES,
  LEASE_LENGTH_OPTIONS,
  SELLING_INTENT_OPTIONS
} from '../lib/constants';

// Step configurations
const STEP_CONFIG = {
  industry: {
    title: 'What best describes your industry?',
    options: INDUSTRIES.map(i => ({ value: i, label: i }))
  },
  subcategory: {
    title: 'What type of business is it?',
    getOptions: (industry) => SUBCATEGORIES[industry]?.map(s => ({ value: s, label: s })) || []
  },
  revenueTrend: {
    title: 'How would you describe your current sales revenue?',
    options: REVENUE_TREND_OPTIONS
  },
  annualRevenue: {
    title: 'What was your total sales revenue for last financial year?',
    helper: 'Total turnover before expenses.',
    type: 'currency'
  },
  ownerEarnings: {
    title: 'What was the total earnings to you for last financial year?',
    description: 'This should include your net profit, any wages paid to an owner plus any other addbacks such as depreciation and personal expenses.',
    helper: 'Think of this as the total financial benefit the business provides to you each year. If your business did not make a profit, enter a negative value (e.g. -10,000).',
    type: 'currency',
    allowNegative: true
  },
  plantEquipment: {
    title: 'What is the approximate resale value of your plant, equipment and fit-out?',
    helper: 'If none, enter 0.',
    type: 'currency'
  },
  state: {
    title: 'Which state is your business located in?',
    options: STATES.map(s => ({ value: s, label: s }))
  },
  hasLease: {
    title: 'Do you operate from leased premises?',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  },
  leaseLength: {
    title: 'How long is left on your lease (including options)?',
    options: LEASE_LENGTH_OPTIONS
  },
  phone: {
    title: 'What is the best phone number to contact you on?',
    description: 'One of our business consultants will be in touch to provide a no obligation chat on your potential sale.',
    type: 'phone',
    optional: false
  },
  sellingIntent: {
    title: 'Are you planning to sell in the next 12 months?',
    options: SELLING_INTENT_OPTIONS
  }
};

// Currency input component
const CurrencyInput = ({ value, onChange, allowNegative, testId, error }) => {
  const [displayValue, setDisplayValue] = useState('');
  
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      setDisplayValue(formatNumberForDisplay(value));
    }
  }, []);
  
  const formatNumberForDisplay = (num) => {
    if (num === '' || num === null || num === undefined) return '';
    const numStr = String(num).replace(/[^0-9.-]/g, '');
    const number = parseFloat(numStr);
    if (isNaN(number)) return '';
    return number.toLocaleString('en-AU');
  };
  
  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Allow negative sign at start if allowed
    const hasNegative = inputValue.startsWith('-');
    
    // Remove all non-numeric characters except decimal
    inputValue = inputValue.replace(/[^0-9.]/g, '');
    
    // Re-add negative if it was there and allowed
    if (hasNegative && allowNegative) {
      inputValue = '-' + inputValue;
    }
    
    // Parse to number
    const numValue = parseFloat(inputValue);
    
    if (inputValue === '' || inputValue === '-') {
      setDisplayValue(inputValue);
      onChange(inputValue === '-' ? -0 : '');
    } else if (!isNaN(numValue)) {
      setDisplayValue(formatNumberForDisplay(numValue));
      onChange(numValue);
    }
  };
  
  return (
    <div className="relative">
      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
      <Input
        type="text"
        inputMode="decimal"
        data-testid={testId}
        value={displayValue}
        onChange={handleChange}
        placeholder="0"
        className={`bonza-input h-14 pl-12 text-lg font-medium ${error ? 'border-red-500' : ''}`}
      />
    </div>
  );
};

// Phone input component - Australian mobile format (04XX XXX XXX, 10 digits)
const PhoneInput = ({ value, onChange, testId, error }) => {
  const handlePhoneChange = (e) => {
    let input = e.target.value;
    
    // Remove all non-digit characters
    let digits = input.replace(/\D/g, '');
    
    // Limit to 10 digits
    digits = digits.slice(0, 10);
    
    // Ensure it starts with 04
    if (digits.length > 0 && !digits.startsWith('0')) {
      digits = '0' + digits.slice(0, 9);
    }
    if (digits.length > 1 && digits[1] !== '4') {
      digits = '04' + digits.slice(2);
    }
    
    // Format as 04XX XXX XXX
    let formatted = digits;
    if (digits.length > 4) {
      formatted = digits.slice(0, 4) + ' ' + digits.slice(4);
    }
    if (digits.length > 7) {
      formatted = digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7);
    }
    
    onChange(formatted);
  };
  
  return (
    <div className="relative">
      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
      <Input
        type="tel"
        inputMode="tel"
        data-testid={testId}
        value={value || ''}
        onChange={handlePhoneChange}
        placeholder="04XX XXX XXX"
        maxLength={12}
        className={`bonza-input h-14 pl-12 text-lg ${error ? 'border-red-500' : ''}`}
      />
      <p className="text-xs text-[#64748B] mt-1">Australian mobile number (10 digits starting with 04)</p>
    </div>
  );
};

// Main Question Step component
export const QuestionStep = ({
  stepKey,
  data,
  formData,
  onUpdate,
  onNext,
  onBack,
  canGoBack,
  isSubmitting = false
}) => {
  const [error, setError] = useState('');
  const config = STEP_CONFIG[stepKey];
  
  // Get options (static or dynamic based on industry)
  const options = config.getOptions 
    ? config.getOptions(formData.industry) 
    : config.options;
  
  const handleSelect = (value) => {
    if (isSubmitting) return;
    const update = { [stepKey]: value };
    onUpdate(update);
    setError('');
    // Auto-advance for selection questions - pass the update for immediate use
    setTimeout(() => onNext(update), 200);
  };
  
  const handleInputChange = (value) => {
    onUpdate({ [stepKey]: value });
    setError('');
  };
  
  const handleContinue = () => {
    // Validation
    if (!config.optional && (data === undefined || data === null || data === '')) {
      setError('Please provide an answer to continue');
      return;
    }
    
    if (config.type === 'currency' && !config.optional) {
      const num = parseFloat(data);
      if (isNaN(num)) {
        setError('Please enter a valid number');
        return;
      }
    }
    
    // Phone validation - must be 10 digits starting with 04
    if (config.type === 'phone' && !config.optional) {
      const digits = (data || '').replace(/\D/g, '');
      if (digits.length !== 10) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }
      if (!digits.startsWith('04')) {
        setError('Please enter a valid Australian mobile number starting with 04');
        return;
      }
    }
    
    onNext();
  };
  
  // Render selection grid
  if (options && options.length > 0) {
    const isLargeGrid = options.length > 8;
    
    return (
      <div className="bonza-form-section" data-testid={`step-${stepKey}`}>
        <div className="bonza-step-header">
          <h2>{config.title}</h2>
          {config.description && <p className="mt-2">{config.description}</p>}
        </div>
        
        <div className={`bonza-options-grid ${isLargeGrid ? '' : 'md:grid-cols-2'}`}>
          {options.map((option) => (
            <SelectableCard
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              selected={data === option.value}
              onClick={handleSelect}
              testId={`option-${option.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            />
          ))}
        </div>
        
        {error && <p className="text-red-500 text-sm mt-4" data-testid="step-error">{error}</p>}
        
        <div className="flex items-center gap-4 mt-8">
          {canGoBack && (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              data-testid="back-btn"
              className="h-12 px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  // Render input field
  return (
    <div className="bonza-form-section" data-testid={`step-${stepKey}`}>
      <div className="bonza-step-header">
        <h2>{config.title}</h2>
        {config.description && (
          <p className="mt-2 text-[#1A1523]">{config.description}</p>
        )}
        {config.helper && (
          <p className="mt-2 text-sm text-[#64748B]">{config.helper}</p>
        )}
      </div>
      
      <div className="space-y-2">
        {config.type === 'currency' && (
          <CurrencyInput
            value={data}
            onChange={handleInputChange}
            allowNegative={config.allowNegative}
            testId={`input-${stepKey}`}
            error={error}
          />
        )}
        
        {config.type === 'phone' && (
          <PhoneInput
            value={data}
            onChange={handleInputChange}
            testId={`input-${stepKey}`}
            error={error}
          />
        )}
      </div>
      
      {error && <p className="text-red-500 text-sm mt-2" data-testid="step-error">{error}</p>}
      
      <div className="flex items-center gap-4 mt-8">
        {canGoBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            data-testid="back-btn"
            className="h-12 px-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}
        <Button
          type="button"
          onClick={handleContinue}
          data-testid="continue-btn"
          className="bonza-btn-primary flex-grow h-12 text-base font-semibold rounded-xl"
        >
          Next
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
};
